import { GLOBAL_ASK_AICA_CONSTANTS } from './constants';
import { GateOutcome, GlobalMessage, GlobalConversationListItem, AskBackGate, CurrentCompany } from './types';
import { CitationSource } from '../dashboard/askAica/citationTypes';
import { AnswerScope } from './answerScopeTypes';
import { ReasoningStep } from '../dashboard/askAica/actions';

/** `conversationKey` addresses the turn's own conversation. Omit it (or pass the active
 * id) to write into the on-screen thread; pass a parked conversation's key and the
 * reducer routes the write into its background buffer instead. */
export function addMessage(message: GlobalMessage, conversationKey?: string | null) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.ADD_MESSAGE, payload: message, conversationKey };
}

/** `sources` and `answerScope` are only ever present on the final chunk (mirrors
 * leads apiHelper::processStreamChunk) — omitted entirely on interim chunks, which
 * is why the reducer merges rather than replaces message.metadata.
 * `conversationKey` routes the patch to a background buffer when the turn's
 * conversation is no longer the one on screen (main's background-thread work). */
export function updateMessage(
    id: number,
    content: string,
    sources?: CitationSource[],
    answerScope?: AnswerScope,
    conversationKey?: string | null,
) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.UPDATE_MESSAGE, payload: { id, content, sources, answerScope }, conversationKey };
}

/** Mirrors the leads setReasoning action (askAica/actions.ts) — accumulates the
 * streamed reasoning message for the in-flight turn onto state.reasoning. */
export function setReasoning(
    reasoning: ReasoningStep[] | null | undefined,
    hasNewReasoning: boolean = false,
    conversationKey?: string | null,
) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.SET_REASONING, payload: { reasoning, hasNewReasoning }, conversationKey };
}

/** Mirrors the leads resetReasoning action — clears state.reasoning before a new turn starts. */
export function resetReasoning() {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.RESET_REASONING };
}

export function initialiseMessageThread(messages: GlobalMessage[]) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.INITIALISE_MESSAGE_THREAD, payload: messages };
}

export function setIsLoading(isLoading: boolean, conversationKey?: string | null) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.SET_IS_LOADING, payload: isLoading, conversationKey };
}

/** Atomically parks the outgoing conversation (only while its turn is still streaming)
 * and restores the incoming one from its background buffer if it has one. One action so
 * park and restore can never interleave with a chunk landing between them. */
export function switchConversation(conversationId: string | null) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.SWITCH_CONVERSATION, payload: conversationId };
}

export function setIsLoadingChatHistory(isLoading: boolean) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.SET_IS_LOADING_CHAT_HISTORY, payload: isLoading };
}

export function setError(error: string | null) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.SET_ERROR, payload: error };
}

/** Dispatched with the raw gate_outcome off the SSE stream — the reducer maps
 * gate_outcome.type to the individual state fields below (see reducer.ts). */
export function setGateOutcome(gateOutcome: GateOutcome) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.SET_GATE_OUTCOME, payload: gateOutcome };
}

export function setCurrentCompany(currentCompany: CurrentCompany | null) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.SET_CURRENT_COMPANY, payload: currentCompany };
}

export function setAskBack(askBack: AskBackGate | null) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.SET_ASK_BACK, payload: askBack };
}

export function setConversationId(conversationId: string | null) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.SET_CONVERSATION_ID, payload: conversationId };
}

/** New Chat — drops the held conversationId + gate/message state so the next send starts fresh. */
export function resetGlobalChat() {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.RESET_GLOBAL_CHAT };
}

/** Clears just the gate-resolution fields (currentCompany/companyLess/askBack/decline) —
 * shared by New Chat and loading a different past conversation, both of which must not
 * carry a stale resolved company into the next turn (see reducer.ts::clearGateState). */
export function resetGateState() {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.RESET_GATE_STATE };
}

export function setConversations(conversations: GlobalConversationListItem[]) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.SET_CONVERSATIONS, payload: conversations };
}

export function setLoadingConversations(isLoading: boolean) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.SET_LOADING_CONVERSATIONS, payload: isLoading };
}

/** Mirrors the leads setIsClearingChat action — true while "Delete Chat" is in
 * flight (F-7 parity). */
export function setIsClearingChat(isClearingChat: boolean) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.SET_IS_CLEARING_CHAT, payload: isClearingChat };
}

/** True once a fetched history page is full, hinting older messages may exist
 * (F-9) — mirrors the leads pagination.hasMoreData semantics. */
export function setHasMoreData(hasMoreData: boolean) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.SET_HAS_MORE_DATA, payload: hasMoreData };
}

/** True from New Chat click until the next turn's first conversation_id lands —
 * mirrors the leads setPendingNewConversation action (D1/D2). */
export function setPendingNewConversation(pending: boolean) {
    return { type: GLOBAL_ASK_AICA_CONSTANTS.SET_PENDING_NEW_CONVERSATION, payload: pending };
}
